import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Tracks the width taken by a scroll container's vertical scrollbar.
 *
 * A scrollbar shrinks the container's content box, so cards inside it end
 * short of anything laid out with the same padding outside it (page headers,
 * filter bars). Reserving the reported gutter as extra right padding on those
 * siblings keeps their trailing control flush with the content's right edge.
 */
export function useScrollbarGutter<T extends HTMLElement>() {
  const nodeRef = useRef<T | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);
  const [gutter, setGutter] = useState(0);

  const ref = useCallback((node: T | null) => {
    cleanupRef.current?.();
    cleanupRef.current = null;
    nodeRef.current = node;
    if (!node) {
      setGutter(0);
      return;
    }

    const measure = () => {
      const width = node.offsetWidth - node.clientWidth;
      setGutter(Number.isFinite(width) && width > 0 ? width : 0);
    };

    // Content growth shows up as a resize of the container's children, so both
    // the container and its children are observed.
    const resizeObserver = new ResizeObserver(measure);
    const observeChildren = () => {
      resizeObserver.disconnect();
      resizeObserver.observe(node);
      for (const child of node.children) resizeObserver.observe(child);
    };
    const mutationObserver = new MutationObserver(observeChildren);

    observeChildren();
    mutationObserver.observe(node, { childList: true });
    measure();

    cleanupRef.current = () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, []);

  useEffect(() => () => cleanupRef.current?.(), []);

  return { ref, gutter };
}
